/**
 * Send this template to one address, to see it arrive.
 *
 * It lives in the template editor rather than under campaigns because that is
 * where the question is asked: you are looking at a template and want to know
 * what it looks like in a real inbox, which a preview cannot tell you — a
 * preview renders the HTML we produced, not the HTML Gmail decided to show.
 *
 * **Three outcomes, and the middle one is the interesting one.**
 *
 *   sent, via aws    a real message left for a real inbox
 *   sent, via stub   nothing left this machine. Said plainly, never implied —
 *                    the slice 3 lesson, where a mock was nearly mistaken for a
 *                    verified integration
 *   not sent         the allow-list refused it. This is a 200, not an error,
 *                    because in a development environment the guard blocking a
 *                    send is the expected answer rather than a fault
 *
 * The panel never claims delivery it cannot evidence. A `stub` success is
 * labelled as a simulation, and a guard refusal explains itself and says what
 * to change — because "nothing happened" with no reason is how someone
 * concludes the product is broken and starts disabling things.
 *
 * **Slice 11 gave it a fourth thing to be honest about.** This is the one screen
 * in the product where "what does a recipient actually get" can be answered by
 * *looking* rather than by reading a description, which makes it the worst place
 * to leave slice 4's promise — *an email Wend sends makes no network request
 * when it is opened* — standing unconditionally. With tracking on, Amazon
 * injects a pixel and rewrites every link after Wend has handed the message
 * over, and neither is visible anywhere in this editor.
 *
 * **This panel adds one line, and deliberately does not repeat the notice above
 * it.** What a message from this workspace carries is stated once, by
 * `whatTheMessageCarries`, at the top of the Edit tab — and this panel is
 * further down that same scroll, not a screen of its own. Rendering the same
 * callout again here would print an identical tone, title and paragraphs twice
 * on one page, which reads as a bug and teaches people to skip the block the
 * second time they meet it; and the second copy is the one that goes stale,
 * because it is the one nobody remembers is there. So the shared description
 * stays at the top and this panel contributes only the sentence that is
 * specific to it: `testSendCheck`, which says **where to look** in the message
 * that arrives.
 *
 * **That line claims nothing about what *this* message will carry**, and the
 * restraint is deliberate: whether a test send is claimed by the workspace's
 * configuration set is decided in `test-send.service.ts` — currently by
 * `trackingSettingsForSend`, whose stored settings arrive with the Integrations
 * half of this slice — and it is not something this screen can observe. A panel
 * that announced "this test carries a tracking pixel" would be asserting the
 * behaviour of API code it cannot see, which is the same species of mistake as
 * the promise being retracted. "Wend cannot show you the pixel, so look in the
 * message that arrives" is true whichever way that decision goes, and a test
 * send exists precisely so the answer need not be taken on trust.
 *
 * ## Slice 17 — a sentence that had been false since slice 6
 *
 * The `aws` outcome read: *"Wend does not learn about either until delivery
 * events are wired up."* Delivery events were wired up in slice 6 and have been
 * counted, deduplicated and drawn on three screens since. Left standing, that
 * sentence told a reader the product does not track bounces **at all**, which is
 * the opposite of true and is exactly the shape the repo rule warns about — a
 * stale justification that argues the next reader into undoing something.
 *
 * The replacement is narrower and is true: `test-send.service.ts` writes one
 * `audit_log` row and, deliberately, **no `messages` row** — a test send has no
 * campaign and `messages.campaign_id` is `NOT NULL` behind a composite FK. So an
 * SNS notification about *this* message has nothing to attach to. Campaign sends
 * are a different path and are tracked. The screen says that now, and says
 * nothing about a feature that already exists.
 *
 * **The three outcomes stayed three.** Guard-blocked says "nothing was sent" in
 * its title rather than only in its body; `stub` still says a simulation left
 * nothing; `aws` leads with *accepted is not delivered* rather than with the
 * word "Sent", because the old title was the strongest claim on the panel and
 * its retraction was in the second sentence. The `SEND_TRANSPORT=aws`
 * instruction is the one thing that folded — it is what a developer does next,
 * not what the outcome means.
 *
 * **The fourth state got a request id.** A request that never came back is not
 * one of the three outcomes, it is a fault, so it renders as a `FormAlert` with
 * the id — this panel's verdict on something just submitted, not a screen that
 * failed to load — which is the only way anybody finds the line in the log.
 */
import { type Template, type TestSendResponse } from '@wend/shared';
import { Button, Callout, Card, CardContent, FormField, Input } from '@wend/ui';
import { Send } from 'lucide-react';
import { useState } from 'react';

import { FormAlert } from '../../components/FormAlert';

import { errorMessage, isApiError } from '../../lib/api/errors';
import { testSend } from '../../lib/api/campaigns';
import { testSendCheck, type TrackingKnowledge } from '../../lib/recipient-experience';
import { MoreDetail } from './MoreDetail';

export interface TestSendPanelProps {
  template: Template;
  /** What this workspace's tracking is set to, read once by the editor. */
  tracking: TrackingKnowledge;
  /** True while a save is in flight — the document must not move under a send. */
  disabled: boolean;
}

export function TestSendPanel({ template, tracking, disabled }: TestSendPanelProps) {
  const [to, setTo] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestSendResponse | null>(null);
  /**
   * The fourth outcome, and it is the one that is genuinely a fault: the request
   * never got an answer it could read. Carries the request id from slice 17,
   * because "the test send could not be attempted" with no id is the one failure
   * on this panel a person cannot diagnose by looking at their inbox.
   */
  const [error, setError] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(
        await testSend({
          templateId: template.id,
          to: to.trim(),
          fromEmail: fromEmail.trim(),
          fromName: fromName.trim() === '' ? null : fromName.trim(),
        }),
      );
    } catch (cause) {
      setError({
        message: errorMessage(cause),
        requestId: isApiError(cause) ? cause.requestId : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  const ready = to.trim() !== '' && fromEmail.trim() !== '';
  const check = testSendCheck(tracking);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-[0.95rem] text-text">
            <Send aria-hidden="true" className="size-4 text-muted" />
            Send a test
          </h2>
          <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
            One message, to one address, through your own Amazon SES account. No campaign and no
            contacts — this template does not need to be approved first.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {/*
            Two separate gates, and a person who knows about one and not the
            other spends an afternoon on it. The allow-list is Wend's; the
            sandbox is Amazon's, and a sandboxed account reaches only addresses
            verified in SES — the single most common "the product is broken"
            report, which is why it is on the field and not behind anything.
          */}
          <FormField
            label="Send to"
            description="Must be on the send allow-list. A sandboxed SES account also reaches only addresses verified in SES."
          >
            <Input
              type="email"
              value={to}
              disabled={disabled || busy}
              placeholder="you@example.com"
              onChange={(event) => {
                setTo(event.target.value);
              }}
            />
          </FormField>

          <FormField
            label="Send from"
            description="Must be an identity verified in your SES account. Amazon refuses anything else."
          >
            <Input
              type="email"
              value={fromEmail}
              disabled={disabled || busy}
              placeholder="hello@yourdomain.com"
              onChange={(event) => {
                setFromEmail(event.target.value);
              }}
            />
          </FormField>
        </div>

        <FormField label="Sender name" description="Optional. What a reader sees instead of the address.">
          <Input
            value={fromName}
            disabled={disabled || busy}
            placeholder="Acme Retail"
            onChange={(event) => {
              setFromName(event.target.value);
            }}
          />
        </FormField>

        <div>
          <Button
            variant="secondary"
            disabled={disabled || busy || !ready}
            onClick={() => {
              void submit();
            }}
          >
            <Send aria-hidden="true" />
            {busy ? 'Sending…' : 'Send test'}
          </Button>
        </div>

        {/*
          Variables are substituted with the same labelled sample values the
          preview uses, because a test send has no contact to read them from.
          Said out loud so nobody reads "Sample Company" in their inbox and
          files a bug.
        */}
        <p className="text-[0.82rem] leading-relaxed text-muted">
          {'{{ variables }}'} are filled with the preview&rsquo;s sample values — there is no
          contact behind a test send.
        </p>

        {/*
          Where to look in the message that arrives — not a second copy of what
          this workspace's mail carries, which is stated once at the top of this
          tab. Above the outcome callouts rather than below them, because it is
          what to do with the test *before* one has been sent, and it stays on
          screen whether or not one has.

          Nothing at all when tracking is off, or while the settings are still
          being read, or when they could not be read: `testSendCheck` returns
          null in each of those, because there is no pixel and no rewritten link
          to go looking for, and pointing somebody at an inbox to hunt for
          something that is not there is worse than saying nothing.
        */}
        {check !== null && <p className="text-[0.82rem] leading-relaxed text-muted">{check}</p>}

        {error !== null && (
          <FormAlert
            tone="error"
            {...(error.requestId === undefined ? {} : { requestId: error.requestId })}
          >
            The test send could not be attempted. {error.message}
          </FormAlert>
        )}

        {/*
          Three outcomes, three callouts, and they are never merged. Guard-blocked
          is a 200 and a `warn`, because in a development environment the
          allow-list refusing a send is the expected answer rather than a fault;
          `stub` is a success that sent nothing; `aws` is Amazon taking the
          message, which is not delivery. Collapsing any two of them is how
          somebody concludes a simulated send reached an inbox.
        */}
        {result !== null && !result.sent && (
          <Callout tone="warn" title="Blocked by the send allow-list — nothing was sent">
            {result.guard.message}
          </Callout>
        )}

        {result !== null && result.sent && result.transport === 'stub' && (
          <Callout tone="info" title="Simulated — nothing was actually sent">
            Wend is on the stub send transport: no message left this machine, no provider was
            contacted, and the id below was made up locally.
            <p className="mt-2 font-mono text-[0.78rem] text-muted">{result.messageId}</p>
            <MoreDetail summary="How to send for real" className="mt-2">
              <p>
                Set <code className="font-mono text-[0.9em]">SEND_TRANSPORT=aws</code> and restart
                the API.
              </p>
            </MoreDetail>
          </Callout>
        )}

        {/*
          "Wend does not learn about either until delivery events are wired up"
          stood here until slice 17 and had been false since slice 6 — delivery
          events are ingested, and a reader could reasonably have concluded that
          bounces are untracked everywhere in the product. The true statement is
          narrower and is about a test send specifically: `test-send.service.ts`
          writes one `audit_log` row and deliberately no `messages` row, so an
          SNS notification about this message has no row to attach to. Campaign
          sends are a different path and are tracked.
        */}
        {result !== null && result.sent && result.transport === 'aws' && (
          <Callout tone="good" title="Amazon accepted it — accepted is not delivered">
            A bounce, a rejection or a spam placement happens after this point. A test send is not
            recorded against a campaign, so Wend does not track what happens to this one — the
            inbox it arrives in is the only evidence.
            <p className="mt-2 font-mono text-[0.78rem] text-muted">{result.messageId}</p>
          </Callout>
        )}
      </CardContent>
    </Card>
  );
}
